/**
 * @lines98/client/components/level-badge — переиспользуемый бейдж уровня игрока.
 *
 * ЗАЧЕМ / WHY THIS EXISTS (meta-jeweler-surfaces.md §2 + §9; owner-directive
 * 2026-05-30 «ствол → ветки»): уровень игрока — широкая аккаунт-ось (odometer всей
 * игры), показывается в НЕСКОЛЬКИХ местах: (a) шапка хаб-Профиля (главное, с прогресс-
 * баром XP); (b) строка лидерборда рядом со своим ником (компакт, только число,
 * display-only — Q-PL-A owner «это достижение игрока»). Это ЕДИНЫЙ модуль бейджа —
 * НЕ копируется по экранам. Профиль и лидерборд монтируют его тонкой веткой.
 *
 * Server-truth (PL-I3, I-1): уровень — серверная правда (`progression.level`); клиент
 * НЕ пересчитывает. В строке лидерборда бейдж = справочная метка достижения, НЕ
 * влияет на сортировку/ранг/тай-брейк (display-only, AC-9). Бейдж-визуал — плейсхолдер
 * (число в гранёной оправе), финальный SVG за art (meta-jeweler-art-assets.md).
 *
 * SCOPE: компактный бейдж (число уровня + опц. титул-флекс) — `createLevelBadge`. NOT
 * IN SCOPE: прогресс-бар XP (он живёт в шапке Профиля — `level-header` ниже,
 * монтируется только там, не в строке борда). Само начисление XP — сервер (Срез 4a).
 *
 * Размеры/токены — внутри (формулы от --fs-control / --cell-size, как другие
 * примитивы); потребитель не верстает бейдж руками. CSS co-located.
 *
 * Cross-references:
 *   - meta-jeweler-surfaces.md §2.1 (бейдж у ника) / §2.2 (прогресс-бар в шапке).
 *   - services/meta-api (ProgressionState — level / xpIntoLevel / xpForLevel).
 *   - components/meta-icons (стиль глифов), components/level-badge.css.
 */

import './level-badge.css';

import { t } from '../lib/i18n/index.js';
import { createValueGuard, signatureOf } from '../lib/reconcile-guard.js';

/** Опции компактного бейджа уровня (число + опц. титул). */
export interface LevelBadgeOptions {
  /** Уровень игрока (server-truth). */
  readonly level: number;
  /**
   * Титул-флекс (`PLAYER_TITLE_BY_MILESTONE`), если открыт. Опц. — owner/art O-PL-9
   * (титулы ещё не зафиксированы; без него бейдж = только число).
   */
  readonly title?: string;
  /** Доп-класс(ы) для контекста (вид не меняют — позиционирование у потребителя). */
  readonly className?: string;
}

export interface LevelBadgeHandle {
  /** Корневой элемент бейджа — потребитель сам его вставляет. */
  readonly element: HTMLElement;
  /** Обновить уровень/титул (после ресинка server-truth). */
  update(opts: { level: number; title?: string }): void;
}

/**
 * Создать компактный бейдж уровня. Возвращает handle; потребитель вставляет
 * `handle.element` (рядом с ником в шапке Профиля / в строке лидерборда).
 */
export function createLevelBadge(opts: LevelBadgeOptions): LevelBadgeHandle {
  const root = document.createElement('span');
  root.className = 'level-badge';
  if (opts.className) root.classList.add(...opts.className.split(/\s+/).filter(Boolean));

  // Гранёная оправа-плейсхолдер (свопабельна на art-SVG) + число уровня внутри.
  const medal = document.createElement('span');
  medal.className = 'level-badge-medal';
  medal.setAttribute('aria-hidden', 'true');
  medal.innerHTML = LEVEL_MEDAL_GLYPH;

  const num = document.createElement('span');
  num.className = 'level-badge-num';

  medal.appendChild(num);
  root.appendChild(medal);

  // Титул-флекс (отдельная строка — НЕ дублирует число; §2.1 «один канал — число»).
  const titleEl = document.createElement('span');
  titleEl.className = 'level-badge-title';
  titleEl.hidden = true;
  root.appendChild(titleEl);

  // Idempotent reconcile guard (instant-hydration §3): бейдж — гидрированная динамика
  // (снимок рисует level, затем reconcile из getMetaState). Если уровень/титул не
  // изменились (server-truth == показанному) — `apply` no-op: DOM не трогаем, бейдж не
  // моргает. Общий «применить-если-изменилось» (lib/reconcile-guard), не копипаст.
  const guard = createValueGuard<string>();

  function apply(level: number, title?: string): void {
    if (!guard.changed(signatureOf(level, title))) return;
    num.textContent = String(level);
    // Доступная подпись несёт смысл (визуально — число в оправе).
    root.setAttribute(
      'aria-label',
      title ? t('game.meta.levelTitleAria', { n: level, title }) : t('game.meta.levelAria', { n: level }),
    );
    if (title) {
      titleEl.textContent = title;
      titleEl.hidden = false;
    } else {
      titleEl.textContent = '';
      titleEl.hidden = true;
    }
  }

  apply(opts.level, opts.title);

  return {
    element: root,
    update(next): void {
      apply(next.level, next.title);
    },
  };
}

/**
 * Гранёный медальон-плейсхолдер (восьмиугольная оправа с фасетками). currentColor
 * через CSS; число рендерит .level-badge-num поверх (не в SVG — чтобы fluid-typography
 * и любая длина числа влезали). Финальный визуал — art (свопабельно).
 */
const LEVEL_MEDAL_GLYPH = `<svg class="level-badge-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><path d="M8 2.5h8l5.5 5.5v8L16 21.5H8L2.5 16V8Z" fill="var(--meta-gold-fill)" stroke="var(--meta-metal-edge)" stroke-width="1.4" stroke-linejoin="round"/><path d="M8 2.5 12 8 16 2.5M2.5 8 8 12 2.5 16M16 21.5 12 16 8 21.5M21.5 16 16 12 21.5 8" stroke="var(--meta-metal-edge)" stroke-width="0.7" opacity="0.4"/></svg>`;

/**
 * Compact level-badge as an HTML STRING (for surfaces that render rows via innerHTML,
 * e.g. the leaderboard's own row — display-only level marker next to the nick, Q-PL-A).
 * Same visual as `createLevelBadge` `--compact`, single-source markup. The level is
 * a small non-negative integer (server-truth) — no escaping needed, but coerced to int.
 *
 * @param level — server-truth player level (display-only; NOT a ranking key, AC-9).
 */
export function levelBadgeHtml(level: number): string {
  const n = Number.isFinite(level) ? Math.max(0, Math.trunc(level)) : 0;
  return `<span class="level-badge level-badge--compact" aria-label="${t('game.meta.levelAria', { n })}"><span class="level-badge-medal" aria-hidden="true">${LEVEL_MEDAL_GLYPH}<span class="level-badge-num">${n}</span></span></span>`;
}
